import {useState} from 'react';
import {thumbsUp, thumbsDown} from '@sail/icons';
import {tokens, Action, Icon, view, useTokenValue} from '@sail/ui';
import {useTheme} from '../ThemeProvider';
import {Theme} from '../theme';

/**
 * This is for the feedback v2 with thumbs up/down non-blocking feedback project.
 * @see https://home.corp.stripe.com/compass/projects/thumbs-up-thumbs-down-on-universal-help
 */
export const FeedbackButtons = ({
  onThumbsUp,
  onThumbsDown,
  value,
}: {
  onThumbsUp: () => Promise<void>;
  onThumbsDown: () => Promise<void>;
  value: 'thumbs_up' | 'thumbs_down' | 'none';
}) => {
  const [isPending, setIsPending] = useState(false);
  const space150 = useTokenValue(tokens.space[150]);
  const theme = useTheme();

  return (
    <view.div
      css={{
        cursor: isPending ? 'default' : 'pointer',
        position: 'absolute',
        marginTop: 'xsmall',
        bottom: `-${space150}`,
        right: tokens.space[150],
        display: 'flex',
        gap: tokens.space.xsmall,
        alignItems: 'center',
        backgroundColor: tokens.color.neutral[0],
        borderRadius: tokens.border.radius.rounded,
        padding: tokens.space.xxsmall,
        boxShadow: tokens.shadow.medium,
        opacity: 0,
        transition: 'opacity 0.2s ease-in-out',
        '.response-id-hover-group:hover &': {
          opacity: 1,
        },
      }}
    >
      <ThumbsAction
        isPending={isPending}
        variant="thumbs_up"
        onPress={() => {
          setIsPending(true);
          onThumbsUp().finally(() => setIsPending(false));
        }}
        isSelected={value === 'thumbs_up'}
        theme={theme}
      />
      <ThumbsAction
        isPending={isPending}
        variant="thumbs_down"
        onPress={() => {
          setIsPending(true);
          onThumbsDown().finally(() => setIsPending(false));
        }}
        isSelected={value === 'thumbs_down'}
        theme={theme}
      />
    </view.div>
  );
};

function ThumbsAction({
  isPending,
  variant,
  onPress,
  isSelected,
  theme,
}: {
  isPending: boolean;
  variant: 'thumbs_up' | 'thumbs_down';
  onPress: () => void;
  isSelected: boolean;
  theme: Theme;
}) {
  const label =
    variant.charAt(0).toUpperCase() + variant.slice(1).replace('_', ' ');
  return (
    <Action
      disabled={isPending}
      aria-label={label}
      onPress={onPress}
      css={{
        paddingRight: tokens.space.xsmall,
        paddingLeft: tokens.space.xsmall,
        cursor: 'inherit',
        backgroundColor: isSelected ? theme.surfaceHoverColor : undefined,
        borderRadius: tokens.border.radius.rounded,
        '--icon-color': isSelected
          ? theme.feedbackThumbColor
          : tokens.color.icon.subdued,
        ':hover': {
          backgroundColor: theme.surfaceHoverColor,
          '--icon-color': isSelected
            ? theme.feedbackThumbColor
            : tokens.color.icon.action.secondary,
        },
      }}
    >
      <Icon
        icon={variant === 'thumbs_up' ? thumbsUp : thumbsDown}
        size="xsmall"
        css={{
          opacity: isPending ? 0.8 : 1,
          fill: 'var(--icon-color)',
        }}
      />
    </Action>
  );
}
